"use client"
import { ShoppingBagIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronsRight, ChevronsUpDown } from "lucide-react";
import Link from "next/link";
import { createElement, type ReactNode } from "react";
import { NotFound } from "~/components/common/not-found";
import { UserNav } from "~/components/common/user-nav";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { Button, buttonVariants } from "~/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "~/components/ui/sheet";
import { calculatePayableCost, cn, formatter, shortName } from "~/lib/utils";
import { adminItems, menuItems } from "~/routes/header.routes";
import { MinusIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { usePathname } from "next/navigation";
import { useClientAuth } from "~/hooks/use-auth";
import useCart from "~/hooks/use-cart";
import Image from "next/image";
import { type ProductProps } from "~/types/types";

interface NavbarSheetProps {
  children: ReactNode
  placement: "top" | "right" | "bottom" | "left"
  title: string
  cart?: boolean
  admin?: boolean
  store?: boolean,
  isOpen: boolean,
  onClose: () => void
}

interface Item {
  title: string;
  link?: string;
  subLink?: string;
}

interface SelectedTabsProps {
  item: Item;
  pathname: string;
}

const selectedTabs = ({ item, pathname }: SelectedTabsProps): string => {
  let select: boolean;

  if (item.title === "Overview") {
    select = pathname === "/admin";
  } else if (item.title === "Home") {
    select = pathname === "/";
  } else {
    const link = item.link ?? item.subLink;
    select = link ? pathname.includes(link) : false;
  }

  return select ? "bg-accent" : "";
};

export function NavbarSheet({ children, placement, title, admin, store, cart, isOpen, onClose }: NavbarSheetProps) {
  const positionButton = title === "Menu" ? "right-3" : "left-3";
  const menuArray = [{ link: "/sign-in", label: "Sign In" }, { link: "/sign-up", label: "Sign Up" }];
  const cartArray = [{ link: "/cart", label: "View Cart", icon: ShoppingBagIcon }, { link: "/checkout", label: "Checkout", icon: ChevronsRight }];
  const pathname = usePathname();
  const { items, addItem } = useCart() as any;

  const getValue = calculatePayableCost(items);

  const { isAuth, isUser, isAdmin } = useClientAuth();

  const modifyCartItems = (product: ProductProps, count: number) => {
    const index = items.findIndex((item: Record<string, string>) => item.productId === product?.id);
    let updatedItems = [];

    if (count > 0) {
      if (index !== -1) {
        updatedItems = items.map((item: Record<string, string>, i: number) =>
          i === index ? { ...item, count } : item,
        );
      } else {
        updatedItems = [...items, { productId: product?.id, product, count }];
      }
    } else {
      updatedItems = items.filter((item: Record<string, string>) => item.productId !== product?.id);
    }

    addItem(updatedItems);
  };

  const onAddToCart = async (product: ProductProps, count: number) => {
    try {
      const newCount = (count ?? 0) + 1;
      modifyCartItems(product, newCount);
    } catch (err) {
      console.log({ err });
    }
  };

  const onRemoveToCart = async (product: ProductProps, count: number) => {
    try {
      const newCount = count - 1;
      modifyCartItems(product, newCount);
    } catch (err) {
      console.log({ err });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={() => onClose()}>
      {/* <SheetTrigger asChild> */}
      {children}
      {/* </SheetTrigger> */}
      <SheetContent side={placement} className={cn("p-0 flex flex-col gap-0 min-w-[350px]")}>
        <SheetHeader className="hidden">
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <div className="flex items-center justify-center p-4 border-b">
          <h5 className="block font-sans text-xl font-semibold leading-snug tracking-normal">
            {title}
          </h5>
          <SheetClose asChild>
            <Button className={cn("absolute! [&_svg]:size-5 size-[40px] rounded-lg", positionButton)} variant="ghost">
              <XMarkIcon className="size-5" />
            </Button>
          </SheetClose>
        </div>
        {(store ?? admin) && (
          <div className="flex h-full w-full flex-1 flex-col overflow-auto">
            <div className="pb-4 pl-4 pr-4 pt-2">
              <nav className="flex flex-col gap-1">
                {(store ? menuItems : adminItems).map((item, index) => (
                  item.subMenu ? (
                    <Accordion key={index} type="single" collapsible defaultValue={pathname.includes("/category") ? "categories" : ""}>
                      <AccordionItem value="categories" className="border-0">
                        <AccordionTrigger className={cn(buttonVariants({ variant: 'ghost' }), "h-auto p-4 justify-between hover:no-underline [&_svg]:size-5", selectedTabs({ item, pathname }))}>
                          <div className="inline-flex">
                            <div className="grid place-items-center mr-2">
                              <item.icon />
                            </div>
                            {item.title}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="p-2 flex flex-col gap-1">
                          {item.subMenuItems.map((item2, index) => (
                            <Link href={`/${item.link}/${item2.subLink}`} key={index}>
                              <SheetClose asChild>
                                <Button className={cn("h-auto [&_svg]:size-5 justify-start w-full p-4", selectedTabs({ item: item2, pathname }))} variant="ghost">
                                  <div className="grid place-items-center mr-2">
                                    <ChevronsRight />
                                  </div>
                                  {item2.title}
                                </Button>
                              </SheetClose>
                            </Link>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <Link href={item.link} key={index}>
                      <SheetClose asChild>
                        <Button className={cn("h-auto [&_svg]:size-5 justify-start w-full p-4", selectedTabs({ item, pathname }))} variant="ghost">
                          <div className="grid place-items-center mr-2">
                            <item.icon />
                          </div>
                          {item.title}
                        </Button>
                      </SheetClose>
                    </Link>
                  )
                ))}
              </nav>
            </div>
          </div>
        )}
        {cart && !items?.length && (
          <NotFound
            content={<ShoppingBagIcon />}
            className="flex-1"
            title="Your cart is currently empty!"
            description="Looks like you haven't made your choice yet."
            titleClassName="font-medium text-lg mt-4 mb-0"
            descriptionClassName="text-sm"
          />
        )}
        {!!cart && items?.length > 0 && (
          <>
            <div className="flex-1 overflow-y-auto px-4">
              <ul className="divide-y">
                {(items || []).map((item: any, index: number) => (
                  <li key={index} className="group flex py-4 relative">
                    <div className="relative size-14">
                      <Image
                        src={item.product?.images?.[0]?.url ?? ""}
                        alt=""
                        loading="lazy"
                        className="rounded-lg object-cover object-center"
                        fill
                      />
                    </div>
                    <div className="flex flex-1 flex-col mx-2 gap-1">
                      <Link href={`/product/${item.product.slug}`}>
                        <h3 className="line-clamp-1 font-semibold text-sm hover:underline">
                          {item.product.title}
                        </h3>
                      </Link>
                      <span className="text-muted-foreground text-xs tabular-nums flex items-center line-clamp-1">
                        {item.product.brand.title}
                        <span className="mx-1">|</span>
                        {item.product.category.title}
                      </span>
                    </div>
                    <div className="flex items-center absolute bottom-4 right-0">
                      <div className="flex flex-1 items-end justify-end text-xs sm:text-sm">
                        <div className="flex items-center overflow-hidden rounded-full border p-0.5 text-xs">
                          <Button className="size-6 rounded-full" variant="ghost" size="icon" onClick={() => onRemoveToCart(item.product, item.count)}>
                            {item.count === 1 ? (
                              <TrashIcon className="text-rose-500" />
                            ) : (
                              <MinusIcon />
                            )}
                          </Button>
                          <span className="px-2">{item.count}</span>
                          <Button className="size-6 rounded-full" variant="ghost" size="icon" onClick={() => onAddToCart(item.product, item.count)}>
                            <PlusIcon />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 border-t">
              <div className="flex justify-between">
                <p className="text-base font-medium">Subtotal</p>
                <p className="text-base font-medium">{formatter.format(Number(getValue?.payableAmount ?? 0))}</p>
              </div>
              <p className="mt-0.5 text-sm text-gray-500">
                Shipping and taxes calculated at checkout.
              </p>
              <div className="flex w-full items-center justify-center gap-2 pt-4">
                {cartArray.map(({ link, label, icon }, index) => (
                  <Link href={link} className="flex flex-1" key={index}>
                    <SheetClose asChild>
                      <Button className="w-full h-auto py-4 rounded-lg text-xs" variant={!index ? "outline" : "default"}>
                        {label}
                        {createElement(icon)}
                      </Button>
                    </SheetClose>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
        {(!!isAuth && !!store) && (
          <div className="p-2 border-t">
            <UserNav mobileNav>
              <Button className="flex w-full items-center gap-2 p-2 h-auto" variant="ghost">
                <Avatar className="rounded-full size-[40px]">
                  <AvatarFallback className="rounded-lg">{shortName(isUser?.name ?? "RK")}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{isUser?.name ?? "Rahul Kumar"}</span>
                  <span className="truncate text-xs">{isUser?.email ?? "rksahu2601@gmail.com"}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </Button>
            </UserNav>
          </div>
        )}
        {(!isAuth && !!store) && (
          <div className="flex w-full items-center justify-center gap-2 border-t p-4">
            {menuArray.map(({ link, label }, index) => (
              <Link href={link} className="flex flex-1" key={index}>
                <SheetClose asChild>
                  <Button className="w-full h-auto py-4 rounded-lg text-xs" variant={!index ? "outline" : "default"}>
                    {label}
                  </Button>
                </SheetClose>
              </Link>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
